/**
 * Public submitter form (loud / paper-grain side).
 *
 * Phase 4 scope:
 *  - All Phase 1 visual fidelity (palette, fonts, layout, marquee, paper grain, tape, floaty tags).
 *  - Real POST /api/submissions via multipart FormData, invisible Turnstile (brief §18).
 *  - Voice mode: MediaRecorder capture, 5-min cap, audio Blob uploads through the
 *    Worker into R2, then Whisper auto-transcribes in the background.
 *  - Rich-text editor for the dilemma (bold / italic / underline / list / colour).
 *  - Loading state, top-of-form error banner, rate-limit message, navigate-on-success.
 *  - Word counter (red at 150+) and the >200-word custom confirm modal.
 *  - Validation messages from PROJECT_BRIEF.md §11.
 *
 * Out of Phase 4 scope:
 *  - Email-policy variants beyond 'optional' (Phase 6).
 *  - Real waveform from recorded audio (Phase 7 nice-to-have per brief §15).
 */

import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

type Mode = 'text' | 'voice';
type RecordState = 'idle' | 'recording' | 'done';

const MAX_RECORD_SECONDS = 300; // 5 minutes per brief §15

const MARQUEE_ITEMS = [
  'SPILL IT',
  'POUR YOUR HEART OUT',
  "WE'RE LISTENING",
  'TELL US EVERYTHING',
  'DROP THE TEA',
  'ASK US ANYTHING',
];

function formatTime(s: number): string {
  const m = Math.floor(s / 60).toString().padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${m}:${ss}`;
}

function countWords(text: string): number {
  // Accept plain text or HTML — strip tags and use textContent for counting.
  const temp = document.createElement('div');
  temp.innerHTML = text ?? '';
  const plain = (temp.textContent || temp.innerText || '').trim();
  if (!plain) return 0;
  return plain.split(/\s+/).length;
}

export default function SubmitterForm() {
  const navigate = useNavigate();

  // ------------ Form state ------------
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [dilemma, setDilemma] = useState('');
  const [mode, setMode] = useState<Mode>('text');
  const [recordState, setRecordState] = useState<RecordState>('idle');
  const [seconds, setSeconds] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState('');
  const [textError, setTextError] = useState('');
  const [voiceError, setVoiceError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [blockedMessage, setBlockedMessage] = useState('');
  const [showWordConfirm, setShowWordConfirm] = useState(false);

  const editorRef = useRef<HTMLDivElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  // The finalised recording Blob lives here so `performSubmit` can attach it
  // to the FormData when voice mode submits.
  const audioBlobRef = useRef<Blob | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);

  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isList, setIsList] = useState(false);
  const [currentColor, setCurrentColor] = useState('#000000');

  const turnstilePromiseRef = useRef<{ promise: Promise<string>; resolve: (t: string) => void } | null>(null);
  const turnstileWidgetIdRef = useRef<string>('');
  const turnstileTokenRef = useRef<string>('');

  // Lazily creates the promise that getTurnstileToken() awaits. Called on mount
  // (so the challenge runs before the user clicks SEND IT IN) and again after
  // each failed POST so a retry gets a fresh token.
  function createTurnstilePromise() {
    let resolver: (t: string) => void = () => {};
    const p = new Promise<string>((res) => (resolver = res));
    turnstilePromiseRef.current = { promise: p, resolve: resolver };
  }

  function resetTurnstile() {
    createTurnstilePromise();
    const tt = (window as any).turnstile;
    if (tt && turnstileWidgetIdRef.current) {
      try {
        tt.reset(turnstileWidgetIdRef.current);
      } catch {
        // ignore
      }
    }
  }

  // Pre-warm Turnstile: render the invisible widget on mount so a token is
  // ready by the time the submitter clicks SEND IT IN. Polls until the
  // Cloudflare script loads.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    createTurnstilePromise();
    let cancelled = false;
    let intervalId: number | null = null;

    function tryRender() {
      const tt = (window as any).turnstile;
      if (!tt || cancelled) return;
      if (turnstileWidgetIdRef.current) return; // already rendered
      try {
        const id = tt.render('#iswis-turnstile', {
          sitekey: TURNSTILE_SITE_KEY,
          size: 'invisible',
          execution: 'render',
          retry: 'auto',
          callback: (token: string) => {
            if (!cancelled) {
              turnstileTokenRef.current = token;
              turnstilePromiseRef.current?.resolve(token);
            }
          },
          'error-callback': () => {
            if (!cancelled) turnstilePromiseRef.current?.resolve('');
            // Prep a fresh promise so a retry can land into it.
            createTurnstilePromise();
          },
          'expired-callback': () => {
            // Token went stale before submit. Turnstile auto-retries (retry: 'auto'),
            // so prep a fresh promise for the incoming new token.
            createTurnstilePromise();
          },
        });
        turnstileWidgetIdRef.current = id;
      } catch {
        // ignore; we'll try again on the next poll tick
        return;
      }
      if (intervalId) window.clearInterval(intervalId);
    }

    if ((window as any).turnstile) {
      tryRender();
    } else {
      intervalId = window.setInterval(tryRender, 150);
    }

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function performSubmit() {
    setSubmitting(true);
    let succeeded = false;
    // Get a Turnstile token (invisible challenge runs here).
    let turnstileToken = '';
    if (TURNSTILE_SITE_KEY) {
      turnstileToken = await getTurnstileToken();
      if (!turnstileToken) {
        setSubmitting(false);
        setSubmitError("We couldn't verify the form. Refresh the page and try again.");
        return;
      }
    } else {
      setSubmitting(false);
      setSubmitError("The form isn't fully set up yet. Try again later.");
      return;
    }

    try {
      // The Worker now accepts multipart/form-data so we can include the audio Blob
      // in voice mode. We use FormData for both modes for a single code path.
      const form = new FormData();
      form.set('name', name.trim());
      if (email.trim()) form.set('email', email.trim());
      form.set('mode', mode);
      form.set('turnstileToken', turnstileToken);
      if (mode === 'text') {
        // `dilemma` is HTML from the rich-text editor; the Worker stores it as-is.
        form.set('textContent', dilemma);
      } else {
        const blob = audioBlobRef.current;
        if (!blob) {
          setSubmitting(false);
          setSubmitError("We lost the recording. Tap re-record and try again.");
          return;
        }
        // File name uses the recorded MIME's typical extension for clarity in R2.
        const ext = blob.type.includes('mp4') ? 'm4a' : blob.type.includes('ogg') ? 'ogg' : 'webm';
        const file = new File([blob], `voicenote.${ext}`, {
          type: blob.type || 'audio/webm',
        });
        form.set('audio', file);
        form.set('voicenoteDurationSeconds', String(seconds));
      }
      const res = await fetch(`${API_BASE}/api/submissions`, {
        method: 'POST',
        // No Content-Type header — letting the browser set the multipart boundary.
        body: form,
        signal: AbortSignal.timeout(mode === 'voice' ? 45_000 : 15_000),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (res.ok && data.ok) {
        succeeded = true;
        navigate('/success');
        return;
      }
      switch (data.error) {
        case 'name_required':
          setNameError("We need something to call you, even if it's 'Anonymous'.");
          setBlockedMessage("We need something to call you, even if it's 'Anonymous'.");
          break;
        case 'text_required':
          setTextError("Don't leave us hanging, what's the dilemma?");
          setBlockedMessage("Don't leave us hanging, what's the dilemma?");
          break;
        case 'audio_required':
        case 'audio_empty':
        case 'duration_required':
          setVoiceError("We didn't catch a recording. Tap to record, then send.");
          setBlockedMessage("We didn't catch a recording. Tap to record, then send.");
          break;
        case 'audio_too_large':
        case 'audio_too_long':
          setVoiceError("That clip is a bit too long. Try a shorter take, under 5 minutes.");
          setBlockedMessage("That clip is a bit too long. Try a shorter take, under 5 minutes.");
          break;
        case 'audio_upload_failed':
          setSubmitError("We couldn't save the recording. Try sending again in a moment.");
          break;
        case 'mode_invalid':
          setSubmitError("Something looks off with the form. Refresh and try again.");
          break;
        case 'turnstile_required':
        case 'turnstile_failed':
          setSubmitError("We couldn't verify the form. Refresh the page and try again.");
          break;
        case 'rate_limited':
          setSubmitError("You've sent a lot today, give it a moment. The babes hear you.");
          break;
        default:
          setSubmitError("Something went wrong on our end. Take a breath and try again in a minute.");
      }
    } catch (err) {
      console.error('submit_failed', err);
      setSubmitError("We couldn't send this in. Check the connection and try again.");
    } finally {
      setSubmitting(false);
      if (!succeeded) resetTurnstile();
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setNameError('');
    setTextError('');
    setVoiceError('');
    setSubmitError('');
    setBlockedMessage('');

    let firstInvalid: 'name' | 'text' | 'voice' | null = null;

    if (!name.trim()) {
      setNameError("We need something to call you, even if it's 'Anonymous'.");
      firstInvalid = 'name';
    }

    if (mode === 'text') {
      // dilemma contains HTML; check text content
      const plainText = (function () {
        const tmp = document.createElement('div');
        tmp.innerHTML = dilemma ?? '';
        return (tmp.textContent || tmp.innerText || '').trim();
      })();
      if (!plainText) {
        setTextError("Don't leave us hanging, what's the dilemma?");
        if (!firstInvalid) firstInvalid = 'text';
      }
    } else {
      // Voice mode: require a completed recording.
      if (recordState !== 'done' || !audioBlobRef.current) {
        setVoiceError(
          "Tap the button and tell us what's going on, or switch to typing."
        );
        if (!firstInvalid) firstInvalid = 'voice';
      }
    }

    if (firstInvalid) {
      // show a visible hint near the submit button so user doesn't have to scroll
      if (firstInvalid === 'name') setBlockedMessage("We need something to call you, even if it's 'Anonymous'.");
      if (firstInvalid === 'text') setBlockedMessage("Don't leave us hanging, what's the dilemma?");
      if (firstInvalid === 'voice') setBlockedMessage("Tap the button and tell us what's going on, or switch to typing.");
      return;
    }

    if (mode === 'text' && wordCount > 200) {
      setShowWordConfirm(true);
      return;
    }

    await performSubmit();
  }

  // Returns the pre-warmed Turnstile token. If the challenge isn't done yet
  // (rare — only if the user submits within ~1s of page load), waits up to 15s.
  // Resolves '' on timeout / error so the caller can surface a clear message.
  function getTurnstileToken(): Promise<string> {
    if (turnstileTokenRef.current) return Promise.resolve(turnstileTokenRef.current);
    const tt = (window as any).turnstile;
    if (tt && turnstileWidgetIdRef.current) {
      try {
        tt.execute(turnstileWidgetIdRef.current);
      } catch {
        // If execute fails, the existing promise/timeout below handles it.
      }
    }
    if (!turnstilePromiseRef.current) return Promise.resolve('');
    return Promise.race([
      turnstilePromiseRef.current.promise,
      new Promise<string>((resolve) => window.setTimeout(() => resolve(''), 5_000)),
    ]);
  }

  const wordCount = countWords(dilemma);

  // ------------ Recording handlers ------------
  async function startRecording() {
    setVoiceError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      recordedChunksRef.current = [];

      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        audioBlobRef.current = blob;
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setRecordState('done');
        // Stop the mic stream once recording is finalised
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecordState('recording');
      setSeconds(0);

      timerRef.current = window.setInterval(() => {
        setSeconds((prev) => {
          const next = prev + 1;
          if (next >= MAX_RECORD_SECONDS) stopRecording();
          return next;
        });
      }, 1000);
    } catch (err) {
      console.error('Mic access failed', err);
      setVoiceError(
        "We couldn't get to your mic. Check browser permissions and try again."
      );
    }
  }

  function stopRecording() {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
  }

  function rerecord() {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    audioBlobRef.current = null;
    setAudioUrl(null);
    setRecordState('idle');
    setSeconds(0);
    setIsPlaying(false);
  }

  function togglePlay() {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play();
      setIsPlaying(true);
    } else {
      a.pause();
      setIsPlaying(false);
    }
  }

  // Update toolbar active states when selection/focus changes inside the editor.
  function rgbToHex(rgb: string) {
    const m = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (!m) return rgb;
    const r = parseInt(m[1], 10);
    const g = parseInt(m[2], 10);
    const b = parseInt(m[3], 10);
    return (
      '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
    );
  }

  function updateFormatState() {
    const el = editorRef.current;
    if (!el) return;
    const sel = document.getSelection();
    if (!sel || !sel.anchorNode || !el.contains(sel.anchorNode)) {
      setIsBold(false);
      setIsItalic(false);
      setIsUnderline(false);
      setIsList(false);
      return;
    }
    try {
      setIsBold(document.queryCommandState('bold'));
      setIsItalic(document.queryCommandState('italic'));
      setIsUnderline(document.queryCommandState('underline'));
      setIsList(document.queryCommandState('insertUnorderedList'));
      const val = document.queryCommandValue && document.queryCommandValue('foreColor');
      if (val) setCurrentColor(val.startsWith('rgb') ? rgbToHex(val) : val);
    } catch {
      // ignore command errors
    }
  }

  // Listen for selection changes and editor events to update toolbar state
  useEffect(() => {
    function onSelectionChange() {
      updateFormatState();
    }
    document.addEventListener('selectionchange', onSelectionChange);
    const el = editorRef.current;
    if (el) {
      el.addEventListener('keyup', onSelectionChange);
      el.addEventListener('mouseup', onSelectionChange);
      el.addEventListener('focus', onSelectionChange);
    }
    return () => {
      document.removeEventListener('selectionchange', onSelectionChange);
      if (el) {
        el.removeEventListener('keyup', onSelectionChange);
        el.removeEventListener('mouseup', onSelectionChange);
        el.removeEventListener('focus', onSelectionChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  

  return (
    <div className="paper-grain min-h-full bg-cream text-burgundy font-hanken text-[17px] leading-[1.55] overflow-x-hidden">
      {/* ---------- Top brand bar ---------- */}
      <div className="relative z-[2] bg-forest px-7 py-[22px]">
        <div
          className="font-rye text-cream-bright text-center tracking-[1px] leading-none"
          style={{ fontSize: 'clamp(22px, 3vw, 32px)' }}
        >
          <DotI>I</DotI> SAID WHAT <DotI>I</DotI> SAID
        </div>
      </div>

      {/* ---------- Marquee ---------- */}
      <div
        className="relative z-[2] overflow-hidden bg-pink py-[14px] border-y-2 border-burgundy"
        aria-hidden="true"
      >
        <div className="flex gap-12 whitespace-nowrap w-max animate-marquee">
          {/* Two copies = seamless loop with translateX(-50%) keyframe */}
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} className="flex items-center gap-12">
              <span
                className="font-bagel text-cream-bright tracking-[0.5px]"
                style={{ fontSize: 'clamp(16px, 1.8vw, 22px)' }}
              >
                {item}
              </span>
              <span className="text-burgundy text-[1.2em]">•</span>
            </span>
          ))}
        </div>
      </div>

      {/* ---------- Hero ---------- */}
      <section className="relative z-[2] mx-auto max-w-[920px] px-6 pt-10 pb-8 text-center">
        <div
          className="floaty hidden md:block absolute top-[50px] left-[4%] -rotate-[8deg] font-bagel bg-pink text-cream-bright px-[14px] py-[6px] rounded-full text-[14px] tracking-[0.5px] border-2 border-burgundy shadow-chunk-3 z-[3]"
        >
          ask us anything
        </div>
        <div
          className="floaty hidden md:block absolute top-[80px] right-[5%] rotate-[7deg] font-bagel bg-forest text-cream-bright px-[14px] py-[6px] rounded-full text-[14px] tracking-[0.5px] border-2 border-burgundy shadow-chunk-3 z-[3]"
        >
          100% anonymous
        </div>

        <img
          src="/images/2.dilemma.jpeg"
          alt="FK and Jollz"
          className="w-full max-w-full max-h-[280px] object-cover rounded-[18px] border-[3px] border-burgundy shadow-chunk-6 mb-9 -rotate-1"
        />

        <h1
          className="font-bagel text-burgundy mb-6 leading-[0.92] tracking-[-0.01em]"
          style={{ fontSize: 'clamp(48px, 9vw, 104px)' }}
        >
          GOT A
          <br />
          <span className="text-pink">DILEMMA?</span>
        </h1>
        <p
          className="text-burgundy-soft font-medium max-w-[620px] mx-auto"
          style={{ fontSize: 'clamp(16px, 1.7vw, 19px)' }}
        >
          Big or small. Serious or silly. Spill it below, we love to hear it.
        </p>
        <p
          className="text-burgundy-soft font-medium max-w-[620px] mx-auto mt-3.5"
          style={{ fontSize: 'clamp(16px, 1.7vw, 19px)' }}
        >
          Fill out the form, or if your situation is just too distressing for you to type,
          send us a voicenote.
        </p>
      </section>

      {/* ---------- Form Card ---------- */}
      <div className="relative z-[2] mx-auto max-w-[720px] px-5 pt-4 pb-20">
        <div className="relative bg-cream-bright border-[3px] border-burgundy rounded-3xl p-2 shadow-chunk-8">
          {/* Tape label */}
          <div
            className="absolute -top-[18px] left-1/2 -translate-x-1/2 -rotate-2 bg-pink text-cream-bright font-bagel px-[22px] py-[6px] text-[14px] tracking-[1.5px] border-2 border-burgundy shadow-chunk-2 whitespace-nowrap"
          >
            HELP US HELP YOU
          </div>

          <form
            onSubmit={handleSubmit}
            className="px-[18px] py-7 sm:px-7 sm:pt-9 sm:pb-8"
            noValidate
          >
            {/* Invisible Turnstile widget. Renders nothing visible; we trigger it on submit. */}
            <div id="iswis-turnstile" />

            {/* Top-of-form error banner (rate limit, network, server, turnstile) */}
            {submitError && (
              <div
                role="alert"
                className="mb-6 px-4 py-3 rounded-2xl border-2 border-[#D4222F] bg-[rgba(212,34,47,0.08)] text-[14px] font-semibold text-[#D4222F]"
              >
                {submitError}
              </div>
            )}

            {/* Name */}
            <div className="mb-6">
              <label
                htmlFor="name"
                className="block font-bold text-[14px] tracking-[0.5px] uppercase text-burgundy mb-2"
              >
                What should we call you?
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError('');
                }}
                placeholder="Anonymous, Big Dawg, Confused in Lekki..."
                className={inputClass(!!nameError)}
              />
              {nameError && <FieldError>{nameError}</FieldError>}
            </div>

            {/* Mode toggle */}
            <div
              className="flex gap-0 bg-cream-deep border-2 border-burgundy rounded-full p-1 mb-7"
              role="tablist"
            >
              <ModeButton active={mode === 'text'} onClick={() => setMode('text')}>
                <ToggleIcon kind="text" />
                Type it out
              </ModeButton>
              <ModeButton active={mode === 'voice'} onClick={() => setMode('voice')}>
                <ToggleIcon kind="voice" />
                Just talk to us
              </ModeButton>
            </div>

            {/* Text mode */}
            {mode === 'text' && (
              <div className="mb-6">
                <label
                  htmlFor="dilemma-text"
                  className="block font-bold text-[14px] tracking-[0.5px] uppercase text-burgundy mb-2"
                >
                  Your dilemma
                </label>
                    {/* Rich text editor: toolbar + contentEditable div */}
                    <div className="mb-2 flex gap-2 items-center">
                      <button
                        type="button"
                        onClick={() => {
                          (document as any).execCommand('bold');
                          updateFormatState();
                        }}
                        className={
                          'px-2 py-1 rounded border border-burgundy/[0.12] hover:bg-cream-bright ' +
                          (isBold ? 'bg-burgundy text-cream-bright' : 'bg-cream text-burgundy')
                        }
                        aria-label="Bold"
                      >
                        <strong>B</strong>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          (document as any).execCommand('italic');
                          updateFormatState();
                        }}
                        className={
                          'px-2 py-1 rounded border border-burgundy/[0.12] hover:bg-cream-bright ' +
                          (isItalic ? 'bg-burgundy text-cream-bright' : 'bg-cream text-burgundy')
                        }
                        aria-label="Italic"
                      >
                        <em>I</em>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          (document as any).execCommand('underline');
                          updateFormatState();
                        }}
                        className={
                          'px-2 py-1 rounded border border-burgundy/[0.12] hover:bg-cream-bright ' +
                          (isUnderline ? 'bg-burgundy text-cream-bright' : 'bg-cream text-burgundy')
                        }
                        aria-label="Underline"
                      >
                        <span style={{ textDecoration: 'underline' }}>U</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          (document as any).execCommand('insertUnorderedList');
                          updateFormatState();
                        }}
                        className={
                          'px-2 py-1 rounded border border-burgundy/[0.12] hover:bg-cream-bright ' +
                          (isList ? 'bg-burgundy text-cream-bright' : 'bg-cream text-burgundy')
                        }
                        aria-label="Bullet list"
                      >
                        •
                      </button>
                      <input
                        aria-label="Text color"
                        type="color"
                        value={currentColor}
                        onInput={(e) => {
                          const v = (e.target as HTMLInputElement).value;
                          (document as any).execCommand('foreColor', false, v);
                          setCurrentColor(v);
                          updateFormatState();
                        }}
                        className="w-9 h-9 p-0 border-none bg-transparent cursor-pointer"
                      />
                    </div>

                    <div className="relative">
                      {countWords(dilemma) === 0 && (
                        <div className="pointer-events-none absolute inset-0 px-[18px] py-4 text-burgundy-faint italic">
                          So... here's what happened. (Take your time, but try to keep it snappy.)
                        </div>
                      )}
                      <div
                        id="dilemma-text"
                        ref={editorRef}
                        role="textbox"
                        contentEditable
                        suppressContentEditableWarning
                        onInput={(e) => {
                          const html = (e.target as HTMLDivElement).innerHTML;
                          setDilemma(html);
                          if (textError) setTextError('');
                          updateFormatState();
                        }}
                        className={
                          'w-full min-h-[200px] px-[18px] py-4 bg-cream border-2 rounded-[14px] font-hanken text-[16px] leading-[1.55] text-ink resize-y transition-[border-color,box-shadow] duration-200 focus:outline-none ' +
                          (textError
                            ? 'border-[#D4222F] shadow-[0_0_0_3px_rgba(212,34,47,0.15)]'
                            : 'border-burgundy focus:border-pink focus:shadow-[0_0_0_3px_rgba(238,58,140,0.2)]')
                        }
                      />
                    </div>
                <div className="flex justify-between mt-2 text-[13px] font-medium text-burgundy-faint">
                  <span
                    className={
                      'transition-colors ' +
                      (wordCount >= 150 ? 'text-[#D4222F] font-bold' : '')
                    }
                  >
                    {wordCount} {wordCount === 1 ? 'word' : 'words'}
                  </span>
                  <span>Short and juicy works best</span>
                </div>
                {textError && <FieldError>{textError}</FieldError>}
              </div>
            )}

            {/* Voice mode */}
            {mode === 'voice' && (
              <div className="text-center pt-3 pb-2">
                {recordState !== 'done' ? (
                  <div className="flex flex-col items-center gap-[18px] mb-2">
                    <button
                      type="button"
                      onClick={recordState === 'idle' ? startRecording : stopRecording}
                      aria-label={
                        recordState === 'recording' ? 'Stop recording' : 'Start recording'
                      }
                      className={
                        'w-[140px] h-[140px] rounded-full text-cream-bright cursor-pointer flex items-center justify-center relative transition-transform duration-150 active:scale-[0.97] hover:scale-[1.04] ' +
                        (recordState === 'recording'
                          ? 'bg-pink border-4 border-burgundy animate-pulse-pink'
                          : 'bg-burgundy border-4 border-pink')
                      }
                    >
                      <MicSolidIcon />
                    </button>
                    <div className="font-bagel text-[22px] text-burgundy">
                      {recordState === 'recording' ? 'Recording... tap to stop' : 'Tap to record'}
                    </div>
                    {recordState === 'recording' && (
                      <div className="font-bagel text-[32px] text-burgundy tracking-[1px]">
                        {formatTime(seconds)}
                      </div>
                    )}
                    <div className="text-[13px] text-burgundy-faint max-w-[320px]">
                      Up to 5 minutes. We'll auto-transcribe so the babes can read it too.
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3.5 w-full max-w-[360px] mx-auto">
                    <div className="w-full bg-cream-deep border-2 border-burgundy rounded-full px-4 py-2.5 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={togglePlay}
                        aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
                        className="w-9 h-9 rounded-full bg-burgundy text-cream-bright border-none cursor-pointer flex items-center justify-center flex-shrink-0"
                      >
                        {isPlaying ? <PauseIcon /> : <PlayIcon />}
                      </button>
                      <div className="flex-1 h-6 flex items-center gap-[2px]">
                        {/* Static decorative waveform — real waveform generation is nice-to-have (brief §15) */}
                        {[30, 60, 45, 80, 55, 70, 40, 90, 50, 65, 35, 75, 55, 45, 60, 80, 50, 70].map(
                          (h, i) => (
                            <span
                              key={i}
                              className="flex-1 bg-burgundy rounded-full"
                              style={{ height: `${h}%` }}
                            />
                          )
                        )}
                      </div>
                      <span className="font-bold text-[13px] text-burgundy">
                        {formatTime(seconds)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={rerecord}
                      className="bg-transparent border-none text-pink-deep font-bold text-[14px] cursor-pointer underline hover:text-burgundy"
                    >
                      ↺ Re-record
                    </button>
                    {audioUrl && (
                      <audio
                        ref={audioRef}
                        src={audioUrl}
                        onEnded={() => setIsPlaying(false)}
                        className="hidden"
                      />
                    )}
                  </div>
                )}
                {voiceError && (
                  <div className="mt-3">
                    <FieldError>{voiceError}</FieldError>
                  </div>
                )}
              </div>
            )}

            {/* Email */}
            <div className="mb-6">
              <label
                htmlFor="email"
                className="block font-bold text-[14px] tracking-[0.5px] uppercase text-burgundy mb-2"
              >
                Email{' '}
                <span className="text-burgundy-faint font-medium normal-case tracking-normal">
                  (optional)
                </span>
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@somewhere.com"
                className={inputClass(false)}
              />
            </div>

            {/* Submit */}
            <div className="mt-8 flex flex-col items-center gap-3.5">
              <button
                type="submit"
                disabled={submitting}
                aria-busy={submitting}
                className="bg-pink text-cream-bright border-[3px] border-burgundy px-12 py-[18px] font-bagel text-[22px] tracking-[1.5px] rounded-full cursor-pointer shadow-chunk-5 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-chunk-7 active:translate-x-[3px] active:translate-y-[3px] active:shadow-[1px_1px_0_#3D1220] transition-[transform,box-shadow] duration-150 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-x-0 disabled:hover:translate-y-0 disabled:hover:shadow-chunk-5"
              >
                {submitting ? 'SENDING...' : 'SEND IT IN'}
              </button>
              {blockedMessage && (
                <div className="mt-3 w-full max-w-[380px] px-4 py-2 rounded-2xl border-2 border-[#D4222F] bg-[rgba(212,34,47,0.08)] text-[14px] font-semibold text-[#D4222F] text-center">
                  {blockedMessage}
                </div>
              )}
              <div className="text-[12px] text-burgundy-faint tracking-[0.5px] uppercase font-semibold">
                Anonymous by default · Read on episodes when juicy
              </div>
            </div>
          </form>
          {/* Word-count confirm modal */}
          {showWordConfirm && (
            <div className="fixed inset-0 z-[300] flex items-center justify-center p-6">
              <div className="absolute inset-0 bg-black/60" />
              <div className="relative z-10 max-w-[520px] w-full bg-[#0f0f0f] text-white rounded-2xl p-6 shadow-2xl">
                <div className="text-[16px] mb-4">{`${wordCount} words, hmmmm Are you sureeee?`}</div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowWordConfirm(false)}
                    className="px-4 py-2 rounded-md bg-[#2d2d2d] text-white border border-white/10 hover:bg-[#3b3b3b]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      setShowWordConfirm(false);
                      await performSubmit();
                    }}
                    className="px-4 py-2 rounded-md bg-cream text-burgundy font-bold hover:bg-cream-bright"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---------- Footer (carousel-style) ---------- */}
      <footer className="relative z-[2] bg-forest text-cream-bright pt-14 pb-9 px-6 text-center">
        <div className="flex justify-center gap-4 mb-9">
          <SocialIcon label="Instagram">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
            </svg>
          </SocialIcon>
          <SocialIcon label="Twitter">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
            </svg>
          </SocialIcon>
          <SocialIcon label="YouTube">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
          </SocialIcon>
          <SocialIcon label="TikTok">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.71a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.14z" />
            </svg>
          </SocialIcon>
        </div>

        {/* Carousel logo (recreation; brief §21 notes we should swap for the real one when we get it) */}
        <CarouselLogo />

        <div className="font-hanken font-bold text-pink text-[14px] mb-1.5">
          The Unsolicited Opinions Podcast
        </div>
        <div className="font-hanken text-cream-bright text-[12.5px] opacity-85">
          © Copyright I Said What I Said 2025
        </div>
      </footer>
    </div>
  );
}

// ---------------- Sub-components ----------------

function DotI({ children }: { children: ReactNode }) {
  return (
    <span className="relative inline-block">
      <span
        className="absolute left-1/2 -translate-x-1/2 w-[7px] h-[7px] bg-pink rounded-full"
        style={{ top: '-10px' }}
        aria-hidden="true"
      />
      {children}
    </span>
  );
}

function inputClass(invalid: boolean) {
  return (
    'w-full px-[18px] py-3.5 bg-cream border-2 rounded-[14px] font-hanken text-[16px] text-ink transition-[border-color,box-shadow] duration-200 placeholder:text-burgundy-faint focus:outline-none ' +
    (invalid
      ? 'border-[#D4222F] shadow-[0_0_0_3px_rgba(212,34,47,0.15)]'
      : 'border-burgundy focus:border-pink focus:shadow-[0_0_0_3px_rgba(238,58,140,0.2)]')
  );
}

function FieldError({ children }: { children: ReactNode }) {
  return (
    <div className="mt-2 text-[13px] font-semibold text-[#D4222F] tracking-[0.2px]">
      {children}
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        'flex-1 px-3.5 py-3 border-none cursor-pointer font-hanken font-bold text-[14px] tracking-[0.5px] rounded-full transition-all duration-[250ms] flex items-center justify-center gap-2 uppercase ' +
        (active
          ? 'bg-burgundy text-cream-bright'
          : 'bg-transparent text-burgundy')
      }
    >
      {children}
    </button>
  );
}

function ToggleIcon({ kind }: { kind: 'text' | 'voice' }) {
  if (kind === 'text') {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-4 h-4"
      >
        <path d="M14 4h6v6" />
        <path d="M10 20H4v-6" />
        <path d="M20 4l-7 7" />
        <path d="M4 20l7-7" />
      </svg>
    );
  }
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-4 h-4"
    >
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <path d="M12 19v3" />
    </svg>
  );
}

function MicSolidIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[50px] h-[50px]">
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2h2v2a5 5 0 0 0 10 0v-2h2z" />
      <path d="M11 19h2v3h-2z" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}

function SocialIcon({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <a
      href="#"
      aria-label={label}
      className="w-12 h-12 rounded-full bg-cream-bright text-burgundy flex items-center justify-center transition-transform duration-200 hover:scale-[1.08] hover:-rotate-[4deg] [&>svg]:w-[22px] [&>svg]:h-[22px]"
    >
      {children}
    </a>
  );
}

function CarouselLogo() {
  const triangleColors = [
    '#EE3A8C', '#2AB89C', '#F08A2E', '#EE3A8C', '#8B5DB5', '#F7B5D4',
    '#EE3A8C', '#F08A2E', '#2AB89C', '#EE3A8C', '#F7B5D4', '#8B5DB5',
  ];
  return (
    <svg className="w-[220px] h-[220px] mx-auto mb-3 block" viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <path
          id="circ-text"
          d="M 150,150 m -118,0 a 118,118 0 1,1 236,0 a 118,118 0 1,1 -236,0"
        />
      </defs>
      <text
        fontFamily="Hanken Grotesk, sans-serif"
        fontWeight="700"
        fontSize="22"
        fill="#FFF5D6"
        letterSpacing="3"
      >
        <textPath href="#circ-text" startOffset="2%">
          POWERED BY CAROUSEL
        </textPath>
      </text>
      <g transform="translate(150 150)">
        {triangleColors.map((color, i) => (
          <g key={i} transform={`rotate(${i * 30})`}>
            <polygon points="0,-90 8,-72 -8,-72" fill={color} />
            <circle cx="0" cy="-100" r="4" fill="#FFF5D6" />
          </g>
        ))}
      </g>
      <circle cx="150" cy="150" r="48" fill="#FFF5D6" />
      <text
        x="150"
        y="173"
        fontFamily="Hanken Grotesk, sans-serif"
        fontWeight="900"
        fontSize="68"
        fill="#0E2A28"
        textAnchor="middle"
      >
        C
      </text>
    </svg>
  );
}
